import {
  LoginCredentials,
  LoginResponse,
  AuthMeResponse,
  AuthSessionResponse,
  LoginErrorCode,
} from '@/entities/auth/model/auth.types';
import { bffApiClient } from '@/shared/lib/apiClient';
import axios, { AxiosError } from 'axios';

const apiClient = bffApiClient;

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // baseURL이 '/api'이므로 경로는 '/v1/auth/login'으로 설정
      const response = await apiClient.post<LoginResponse>('/v1/auth/login', credentials);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorResponse = (
          error as AxiosError<{
            error?: { code?: string; message?: string; field?: string };
            code?: string;
            message?: string;
            field?: string;
          }>
        ).response?.data;
        return {
          success: false,
          error: {
            code: (errorResponse?.error?.code ||
              errorResponse?.code ||
              'SERVER_ERROR') as LoginErrorCode,
            message:
              errorResponse?.error?.message || errorResponse?.message || '로그인에 실패했습니다.',
            field: (errorResponse?.error?.field || errorResponse?.field || 'general') as
              | 'general'
              | 'email'
              | 'password'
              | undefined,
          },
        };
      }

      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: '알 수 없는 오류가 발생했습니다.',
          field: 'general',
        },
      };
    }
  },

  /**
   * 인증 정보 조회
   * 쿠키의 토큰을 사용하여 현재 인증된 사용자 정보를 가져옵니다.
   */
  async getMe(): Promise<AuthMeResponse> {
    try {
      const response = await apiClient.get<AuthMeResponse>('/v1/auth/me');
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  },

  /**
   * 경량 세션 (외부 /me만). 토큰 갱신 직후 getMe 대신 사용.
   */
  async getSession(): Promise<AuthSessionResponse> {
    try {
      const response = await apiClient.get<AuthSessionResponse>('/v1/auth/session');
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw new Error('세션 정보를 가져오는데 실패했습니다.');
    }
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/v1/auth/logout');
    } catch (error) {
      // 로그아웃 실패해도 무시 (이미 쿠키가 삭제되었을 수 있음)
      console.error('Logout error:', error);
    }
  },

  /**
   * 토큰 갱신
   * 쿠키의 refresh token을 사용하여 새 access token을 발급받습니다.
   */
  async refreshToken(): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/v1/auth/refresh', {});
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorResponse = (
          error as AxiosError<{
            error?: { code?: string; message?: string; field?: string };
            code?: string;
            message?: string;
            field?: string;
          }>
        ).response?.data;
        return {
          success: false,
          error: {
            code: (errorResponse?.error?.code ||
              errorResponse?.code ||
              'SERVER_ERROR') as LoginErrorCode,
            message:
              errorResponse?.error?.message ||
              errorResponse?.message ||
              '토큰 갱신에 실패했습니다.',
            field: (errorResponse?.error?.field || errorResponse?.field || 'general') as
              | 'general'
              | 'email'
              | 'password'
              | undefined,
          },
        };
      }

      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: '알 수 없는 오류가 발생했습니다.',
          field: 'general',
        },
      };
    }
  },
};
